import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import {
  adminAPI,
  AdminAskAnalytics,
  AdminLogEntry,
  AdminMarketplaceListingRow,
  AdminMarketplaceOrderRow,
  AdminOverview,
  AdminRagDocumentRow,
  AdminUserRow,
  formatPriceCents,
  getUserErrorMessage,
} from '../api/api';

type AdminTab = 'overview' | 'users' | 'marketplace' | 'content' | 'ask' | 'logs';

function formatWhen(value: string): string {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const Admin: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>('overview');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [askStats, setAskStats] = useState<AdminAskAnalytics | null>(null);
  const [listings, setListings] = useState<AdminMarketplaceListingRow[]>([]);
  const [orders, setOrders] = useState<AdminMarketplaceOrderRow[]>([]);
  const [documents, setDocuments] = useState<AdminRagDocumentRow[]>([]);
  const [logs, setLogs] = useState<AdminLogEntry[]>([]);
  const [rulesJson, setRulesJson] = useState('[\n  {\n    "slug": "example-rule",\n    "category": "other",\n    "title": "Example",\n    "body": "Rule body",\n    "source": "Admin panel"\n  }\n]');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [ingestMessage, setIngestMessage] = useState('');

  const run = useCallback(async (loader: () => Promise<void>) => {
    setLoading(true);
    setError('');
    try {
      await loader();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Admin request failed.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOverview = useCallback(() => {
    return run(async () => {
      setOverview(await adminAPI.overview());
    });
  }, [run]);

  const loadUsers = useCallback(
    (q?: string) => {
      return run(async () => {
        const res = await adminAPI.listUsers({ q: q ?? userQuery, limit: 50 });
        setUsers(res.items);
      });
    },
    [run, userQuery]
  );

  const loadAsk = useCallback(() => {
    return run(async () => {
      setAskStats(await adminAPI.askAnalytics(14));
    });
  }, [run]);

  const loadMarketplace = useCallback(() => {
    return run(async () => {
      const [listingRes, orderRes] = await Promise.all([
        adminAPI.listListings({ limit: 50 }),
        adminAPI.listOrders({ limit: 50 }),
      ]);
      setListings(listingRes.items);
      setOrders(orderRes.items);
    });
  }, [run]);

  const loadContent = useCallback(() => {
    return run(async () => {
      const res = await adminAPI.listDocuments({ limit: 50 });
      setDocuments(res.items);
    });
  }, [run]);

  const loadLogs = useCallback(() => {
    return run(async () => {
      const res = await adminAPI.logs(250);
      setLogs(res.items);
    });
  }, [run]);

  useEffect(() => {
    if (tab === 'overview') {
      loadOverview();
    } else if (tab === 'users') {
      loadUsers('');
    } else if (tab === 'ask') {
      loadAsk();
    } else if (tab === 'marketplace') {
      loadMarketplace();
    } else if (tab === 'content') {
      loadContent();
    } else if (tab === 'logs') {
      loadLogs();
    }
  }, [tab, loadOverview, loadUsers, loadAsk, loadMarketplace, loadContent, loadLogs]);

  const handleUserSearch = (e: FormEvent) => {
    e.preventDefault();
    loadUsers(userQuery);
  };

  const handleRulesIngest = async (e: FormEvent) => {
    e.preventDefault();
    setIngestMessage('');
    setError('');
    try {
      const rules = JSON.parse(rulesJson);
      if (!Array.isArray(rules)) {
        throw new Error('Rules JSON must be an array.');
      }
      const result = await adminAPI.ingestRules(rules);
      setIngestMessage(
        `Rules ingest complete — created ${result.created}, updated ${result.updated}, skipped ${result.skipped}.`
      );
      loadContent();
    } catch (err: unknown) {
      setError(
        getUserErrorMessage(err, 'Rule ingest failed. Check JSON shape and try again.')
      );
    }
  };

  const handleDeleteDocument = async (doc: AdminRagDocumentRow) => {
    const label = doc.title || doc.source_filename;
    if (!window.confirm(`Remove “${label}” from the research library? This cannot be undone.`)) {
      return;
    }
    setError('');
    setLoading(true);
    try {
      await adminAPI.deleteDocument(doc.id);
      setIngestMessage(`Removed “${label}” from the research library.`);
      await loadContent();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not remove document.'));
    } finally {
      setLoading(false);
    }
  };

  const handlePdfIngest = async (e: FormEvent) => {
    e.preventDefault();
    if (!pdfFile) {
      setError('Choose a PDF file first.');
      return;
    }
    setIngestMessage('');
    setError('');
    setLoading(true);
    try {
      const result = await adminAPI.ingestPdf(pdfFile);
      setIngestMessage(
        `PDF ingest complete — created ${result.created}, updated ${result.updated}, skipped ${result.skipped}.`
      );
      setPdfFile(null);
      loadContent();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'PDF ingest failed.'));
    } finally {
      setLoading(false);
    }
  };

  const tabs: { id: AdminTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'marketplace', label: 'Marketplace' },
    { id: 'content', label: 'Content' },
    { id: 'ask', label: 'Ask analytics' },
    { id: 'logs', label: 'Logs' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="container"
      style={{ padding: '32px 16px 64px', maxWidth: '1100px', margin: '0 auto' }}
    >
      <h1 style={{ color: 'var(--neon-cyan)', marginBottom: '8px' }}>Admin control center</h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
        Platform overview, users, marketplace, research ingest, Ask usage, and API logs.
        Your account has unlimited Ask and full marketplace access.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`btn ${tab === id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="error">{error}</div>}
      {ingestMessage && (
        <div className="success" role="status" style={{ marginBottom: '16px' }}>
          {ingestMessage}
        </div>
      )}
      {loading && (
        <div style={{ marginBottom: '16px' }}>
          <div className="spinner" />
        </div>
      )}

      {tab === 'overview' && overview && (
        <div className="card" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
          {[
            ['Users', overview.user_count],
            ['Verified', overview.verified_user_count],
            ['Admins', overview.admin_count],
            ['RAG documents', overview.rag_document_count],
            ['Active listings', overview.active_listing_count],
            ['Open orders', overview.open_order_count],
            ['Ask questions today', overview.ask_questions_today],
            ['Ask tokens today', overview.ask_tokens_today],
          ].map(([label, value]) => (
            <div key={String(label)}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{label}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div className="card">
          <form onSubmit={handleUserSearch} style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input
              className="form-input"
              placeholder="Search name or email"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              style={{ flex: '1 1 220px' }}
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </form>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color, #333)' }}>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Verified</th>
                  <th>Admin</th>
                  <th>Subscription</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.email_verified ? 'Yes' : 'No'}</td>
                    <td>{u.is_admin ? 'Yes' : 'No'}</td>
                    <td>{u.subscription_status ?? '—'}</td>
                    <td>{formatWhen(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'marketplace' && (
        <div style={{ display: 'grid', gap: '20px' }}>
          <div className="card">
            <h2 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Listings</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Price</th>
                    <th>Seller</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((row) => (
                    <tr key={row.id}>
                      <td>{row.title}</td>
                      <td>{row.status}</td>
                      <td>{formatPriceCents(row.price_cents)}</td>
                      <td>
                        {row.seller_name} ({row.seller_email})
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card">
            <h2 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Orders</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th>Listing</th>
                    <th>Status</th>
                    <th>Buyer</th>
                    <th>Seller</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((row) => (
                    <tr key={row.id}>
                      <td>{row.listing_title}</td>
                      <td>{row.status}</td>
                      <td>{row.buyer_name}</td>
                      <td>{row.seller_name}</td>
                      <td>{formatPriceCents(row.price_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'content' && (
        <div style={{ display: 'grid', gap: '20px' }}>
          <div className="card">
            <h2 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Upload PDF</h2>
            <form onSubmit={handlePdfIngest}>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                className="form-input"
              />
              <button type="submit" className="btn btn-primary" style={{ marginTop: '12px' }} disabled={loading}>
                Ingest PDF
              </button>
            </form>
          </div>
          <div className="card">
            <h2 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Ingest rules (JSON array)</h2>
            <form onSubmit={handleRulesIngest}>
              <textarea
                className="form-input"
                rows={10}
                value={rulesJson}
                onChange={(e) => setRulesJson(e.target.value)}
                style={{ fontFamily: 'monospace', width: '100%' }}
              />
              <button type="submit" className="btn btn-primary" style={{ marginTop: '12px' }}>
                Ingest rules
              </button>
            </form>
          </div>
          <div className="card">
            <h2 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Indexed documents</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {documents.map((doc) => (
                <li
                  key={doc.id}
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                    alignItems: 'flex-start',
                  }}
                >
                  <span>
                    <strong>{doc.title}</strong> — {doc.category} — {doc.source_filename} ({doc.page_count}{' '}
                    pp.)
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ flexShrink: 0 }}
                    disabled={loading}
                    onClick={() => handleDeleteDocument(doc)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'ask' && askStats && (
        <div className="card">
          <p>
            Today: {askStats.questions_today} questions, {askStats.tokens_today} tokens,{' '}
            {askStats.active_users_today} active users.
          </p>
          <table style={{ width: '100%', marginTop: '16px', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th>Date (UTC)</th>
                <th>Questions</th>
                <th>Tokens</th>
                <th>Active users</th>
              </tr>
            </thead>
            <tbody>
              {askStats.daily.map((row) => (
                <tr key={row.usage_date}>
                  <td>{row.usage_date}</td>
                  <td>{row.question_count}</td>
                  <td>{row.token_count}</td>
                  <td>{row.active_users}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'logs' && (
        <div className="card">
          <button type="button" className="btn btn-secondary" onClick={() => loadLogs()} style={{ marginBottom: '12px' }}>
            Refresh logs
          </button>
          <pre
            style={{
              maxHeight: '480px',
              overflow: 'auto',
              fontSize: '0.75rem',
              background: 'rgba(0,0,0,0.35)',
              padding: '12px',
              borderRadius: '8px',
            }}
          >
            {logs.length === 0
              ? 'No log entries captured yet (INFO+ from this API process).'
              : logs
                  .map(
                    (entry) =>
                      `[${entry.timestamp}] ${entry.level} ${entry.logger}: ${entry.message}`
                  )
                  .join('\n')}
          </pre>
        </div>
      )}
    </motion.div>
  );
};

export default Admin;
